import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './index.css';
import { VictoryCandlestick, VictoryGroup, VictoryScatter } from 'victory';
import { colors } from '../../../../../../../colors';
import { GenericTransactionInfo } from '../../../../../../../types/Transaction';
import { ComponentDimensions } from '../../../../../../../types/Layout';

interface Props {
  transaction: GenericTransactionInfo;
  compactTransactions: GenericTransactionInfo[];
}

const TransactionItemCandlesChart = (props: Props) => {
  const { transaction, compactTransactions } = props;
  const rootRef = useRef<HTMLDivElement | null>(null);

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >();

  const candleDataPoints = useMemo(() => {
    if (
      typeof transaction.candles === 'undefined' ||
      transaction.candles?.higher.length === 0
    ) {
      return [];
    }

    const dataPoints = transaction.candles.higher.map((c) => {
      return {
        ...c,
        x: c.startTimestamp,
      };
    });

    return dataPoints;
  }, [transaction.candles]);

  const findClosestCandleTimestamp = useCallback(
    (timestamp: number) => {
      if (!transaction.candles) {
        return timestamp;
      }

      const { timestamp: nearbyTimestamp } = transaction.candles.higher.reduce(
        (acc, curr) => {
          const diff = Math.abs(timestamp - curr.startTimestamp);

          if (diff < acc.min && curr.startTimestamp <= timestamp) {
            acc.min = diff;
            acc.timestamp = curr.startTimestamp;
          }

          return acc;
        },
        { min: Infinity, timestamp: 0 },
      );

      return nearbyTimestamp ?? timestamp;
    },
    [transaction.candles],
  );

  const transactionDataPoints = useMemo(() => {
    return compactTransactions.map((t) => ({
      x: findClosestCandleTimestamp(t.transactionTimestamp || 0),
      y: t.orderPrice,
      color: t.orderType === 'BUY' ? colors.green : colors.red,
      strokeColor: t.transactionType.includes('STOP_LOSS')
        ? colors.blue
        : t.transactionType.includes('TAKE_PROFIT')
        ? colors.yellow
        : colors.white,
      size: t.transactionType.includes('STOP_LOSS')
        ? 3
        : t.transactionType.includes('TAKE_PROFIT')
        ? 4
        : 7,
    }));
  }, [compactTransactions, findClosestCandleTimestamp]);

  const getCandleStrokeColor = useCallback(
    (c: any) => (c.datum.close >= c.datum.open ? 'green' : 'red'),
    [],
  );

  useEffect(() => {
    if (!!rootDimensions) {
      return;
    }

    if (!rootRef.current) {
      return;
    }

    setRootDimensions({
      height: 300,
      width: rootRef.current.clientWidth,
    });
  }, [rootDimensions]);

  if (!transaction.candles || transaction.candles.higher.length === 0) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      style={{
        width: rootDimensions?.width,
        height: rootDimensions?.height,
      }}
    >
      {rootDimensions && (
        <VictoryGroup
          domainPadding={{ x: 30 }}
          padding={10}
          colorScale={undefined}
          width={rootDimensions.width}
          height={rootDimensions.height}
        >
          <VictoryCandlestick
            data={candleDataPoints}
            candleColors={{ positive: colors.green, negative: colors.red }}
            style={{
              data: {
                stroke: getCandleStrokeColor,
                strokeWidth: 0.4,
                fill: getCandleStrokeColor,
              },
            }}
          />
          <VictoryScatter
            data={transactionDataPoints}
            style={{
              data: {
                fill: ({ datum }) => datum.color,
                stroke: (d) => d.datum.strokeColor,
                strokeWidth: 2,
              },
            }}
            size={({ datum }) => datum.size}
          />
        </VictoryGroup>
      )}
    </div>
  );
};

export default TransactionItemCandlesChart;
